// src/data/ingestionEventsClient.js
//
// INGESTION-HISTORY-S1 — read-only client for GET /api/ingestion-events.
//
// Returns { summary, events } as documented in the server route.
// Viewer-readable; no audit emission (reads only).
//
// INGESTION-HISTORY-REVERT — admin-only revert preview and execution.
//   getIngestionEventRevertPreview(id) — GET /api/ingestion-events/:id/revert-preview
//   revertIngestionEvent(id)           — POST /api/ingestion-events/:id/revert

import { NetworkError, errorFromResponse } from "./errors.js";
import { log } from "../utils/logger.js";

async function jsonRequest(url, opts = {}) {
  let res;
  try {
    res = await fetch(url, {
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      ...opts,
    });
  } catch (err) {
    log.error("ingestionEventsClient: network failure", { url, err: String(err) });
    throw new NetworkError(`Failed to reach ${url}`, err);
  }
  if (!res.ok) {
    const e = await errorFromResponse(res);
    log.warn("ingestionEventsClient: non-2xx", { url, status: res.status, name: e.name });
    throw e;
  }
  try {
    return await res.json();
  } catch (err) {
    throw new NetworkError(`Malformed JSON from ${url}`, err);
  }
}

/**
 * @param {{ type?: string, dataset?: string, status?: string, since?: string }} [filters]
 * @returns {Promise<{ summary: object, events: object[] }>}
 */
export async function listIngestionEvents(filters = {}) {
  const params = new URLSearchParams();
  if (filters.type)    params.set("type",    filters.type);
  if (filters.dataset) params.set("dataset", filters.dataset);
  if (filters.status)  params.set("status",  filters.status);
  if (filters.since)   params.set("since",   filters.since);

  const qs  = params.toString();
  const url = qs ? `/api/ingestion-events?${qs}` : "/api/ingestion-events";

  const data = await jsonRequest(url, { method: "GET" });
  log.api("ingestionEventsClient.listIngestionEvents fetched", {
    total: data?.events?.length ?? 0,
  });
  return data;
}

/**
 * Admin-only: preview how many rows would be removed by reverting this event.
 * Returns 409 ConflictError when the event is not Applied or has been
 * partially superseded by a later import.
 *
 * @param {number} id
 * @returns {Promise<{
 *   rowCount: number,
 *   dataset: string,
 *   occurredAt: string,
 *   superseded: boolean,
 *   supersededMessage: string | null
 * }>}
 */
export async function getIngestionEventRevertPreview(id) {
  const data = await jsonRequest(`/api/ingestion-events/${id}/revert-preview`, { method: "GET" });
  log.api("ingestionEventsClient.getIngestionEventRevertPreview", { id, rowCount: data?.rowCount });
  return data;
}

/**
 * Admin-only: execute revert for an Applied ingestion event.
 * Deletes/restores all rows belonging to the linked import session, then
 * flips the event status to 'Reverted'.
 * Returns 409 ConflictError when the session has been partially superseded.
 *
 * @param {number} id
 * @returns {Promise<{ rowsRemoved: number }>}
 */
export async function revertIngestionEvent(id) {
  const data = await jsonRequest(`/api/ingestion-events/${id}/revert`, { method: "POST" });
  log.api("ingestionEventsClient.revertIngestionEvent completed", { id, rowsRemoved: data?.rowsRemoved });
  return data;
}

/**
 * Admin-only bulk clear: hard-deletes every Discarded ingestion_events row
 * (plus the matching discarded import_sessions rows).
 * @returns {Promise<{ eventsDeleted: number, sessionsDeleted: number }>}
 */
export async function clearDiscardedIngestionEvents() {
  const data = await jsonRequest("/api/ingestion-events/discarded", { method: "DELETE" });
  log.api("ingestionEventsClient.clearDiscardedIngestionEvents cleared", data);
  return data;
}

/**
 * Admin-only per-row clear: hard-deletes a single Discarded ingestion_events
 * row (and its matching discarded import_sessions row, if any).
 * @param {number} id
 * @returns {Promise<{ deleted: boolean, id: number, sessionDeleted: boolean }>}
 */
export async function clearIngestionEvent(id) {
  const data = await jsonRequest(`/api/ingestion-events/${id}`, { method: "DELETE" });
  log.api("ingestionEventsClient.clearIngestionEvent cleared", { id });
  return data;
}
